import {ChangeEvent, FC, memo, useCallback, useState} from "react";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography, useMediaQuery, useTheme
} from "@mui/material";
import {HeaderRow, IOSSwitch, PairCard, StatusTextMultiline} from "./ConnectionPairCard.styled";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinearProgress from '@mui/material/LinearProgress';
import {Props} from "./types";
import {ExpandMore} from "../ExpandMore";
import {CollapsibleBody} from "./components/CollapsibleBody";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TuneIcon from "@mui/icons-material/Tune";
import { SpreadMoverSettings } from "../SpreadMoverSettings";
import { AlgorithmModeControl } from "../AlgorithmModeControl";

export const ConnectionPairCard: FC<Props> = memo(({ pair, status, onToggle, onSelect, onDelete }) => {
    // Локальный стейт для открытия/закрытия
    const [expanded, setExpanded] = useState(false);
    const [spreadMoverOpen, setSpreadMoverOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState("");
    const [deleting, setDeleting] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // Колбэк для переключения раскрытия
    const handleExpandClick = useCallback(() => {
        setExpanded(prev => !prev);
    }, []);

    const handleSelect = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            onSelect(pair);
        },
        [onSelect, pair]
    );

    const handleToggle = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            e.stopPropagation();
            onToggle(pair.id, e.target.checked);
        },
        [onToggle, pair.id]
    );

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await onDelete(pair);
            setDeleteOpen(false);
            setDeleteConfirmation("");
        } catch {
            // The parent shows the API error and the dialog stays open for retry.
        } finally {
            setDeleting(false);
        }
    };

    return (
        <PairCard key={pair.id}>
            <HeaderRow onClick={handleExpandClick}>
                <Avatar sx={{ bgcolor: "#f5a623", width: 40, height: 40, marginRight: 2 }}>
                    {pair.pair_name[0]}
                </Avatar>

                <Box flex={1}>
                    <Typography variant="h6">{pair.pair_name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {pair.exchange} | {pair.is_active ? "Online" : "Offline"}
                    </Typography>
                    <Box sx={{ mt: 0.5 }} maxWidth={ isMobile ? 145 : 650 }>
                        <Typography variant="body2" color="text.secondary" mb={1}>
                            Status:
                        </Typography>
                        {status ? (
                            <StatusTextMultiline>{status === 'RISK_PAUSED' ? 'Защитная пауза — проверьте исполнения в настройках MM' : status}</StatusTextMultiline>
                        ) : (
                            <LinearProgress/>
                        )}
                    </Box>
                </Box>

                <Stack direction="column" spacing={1} alignItems="stretch" onClick={(e) => e.stopPropagation()}>
                    <IOSSwitch
                        checked={pair.is_active}
                        onChange={handleToggle}
                        inputProps={{ "aria-label": "toggle state" }}
                    />
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={handleSelect}
                    >
                        Manage
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<TuneIcon />}
                        onClick={() => setSpreadMoverOpen(true)}
                    >
                        Spread mover
                    </Button>
                    <AlgorithmModeControl
                        connectionId={pair.id}
                        pairLabel={`${pair.exchange} ${pair.pair_name}`}
                        isConnectionActive={pair.is_active}
                    />
                    <Tooltip title={pair.is_active ? "Сначала выключите пару" : "Удалить пару без возможности восстановления"}>
                        <span>
                            <IconButton
                                color="error"
                                size="small"
                                disabled={pair.is_active}
                                onClick={() => setDeleteOpen(true)}
                                aria-label={`Удалить ${pair.pair_name}`}
                                sx={{ width: "100%", borderRadius: 1 }}
                            >
                                <DeleteOutlineIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>

                <ExpandMore
                    expand={expanded}
                    onClick={e => {
                        e.stopPropagation();
                        handleExpandClick();
                    }}
                    aria-expanded={expanded}
                    aria-label="show more"
                >
                    <ExpandMoreIcon />
                </ExpandMore>
            </HeaderRow>

            {/* Выпадающая область с анимацией */}
            <Collapse in={expanded} timeout="auto" unmountOnExit style={{ width: "100%", marginTop: 20 }}>
                <CollapsibleBody compactMode={isMobile} />
            </Collapse>

            <Dialog
                open={spreadMoverOpen}
                onClose={() => setSpreadMoverOpen(false)}
                fullWidth
                maxWidth="md"
                scroll="paper"
            >
                <DialogTitle>Настройки {pair.exchange} · {pair.pair_name}</DialogTitle>
                <DialogContent dividers>
                    <SpreadMoverSettings
                        connectionId={pair.id}
                        pairLabel={`${pair.exchange} ${pair.pair_name}`}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSpreadMoverOpen(false)}>Закрыть</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)}>
                <DialogTitle color="error">Безвозвратно удалить пару?</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 0.5 }}>
                        <Alert severity="error">
                            Будут удалены подключение, его настройки и сохранённая история. Отменить эту операцию невозможно.
                        </Alert>
                        <DialogContentText>
                            Пара: <strong>{pair.exchange} · {pair.pair_name}</strong>. Ордера, уже находящиеся на бирже,
                            автоматически не отменяются.
                        </DialogContentText>
                        <TextField
                            autoFocus
                            fullWidth
                            label={`Введите ${pair.pair_name} для подтверждения`}
                            value={deleteConfirmation}
                            onChange={(event) => setDeleteConfirmation(event.target.value)}
                            disabled={deleting}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>Отмена</Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={deleting || deleteConfirmation !== pair.pair_name}
                        onClick={handleDelete}
                    >
                        {deleting ? "Удаление…" : "Удалить навсегда"}
                    </Button>
                </DialogActions>
            </Dialog>
        </PairCard>
    );
});
